import { useMutation, useQuery } from '@apollo/client'
import { css, StyleSheet } from 'aphrodite'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useState } from 'react'
import ReactPixel from 'react-facebook-pixel'
import Geocode from 'react-geocode'
import GooglePlacesAutocomplete from 'react-google-places-autocomplete'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import LiveContext from '../../components/Context/LiveContext'
import LayoutCol from '../../components/Layout/LayoutCol'
import LayoutRow from '../../components/Layout/LayoutRow'
import Map from '../../components/Map/Map'
import UIButton from '../../components/UI/UIButton'
import UICheckbox from '../../components/UI/UICheckbox'
import UIDatePicker from '../../components/UI/UIDatePicker'
import UIInput from '../../components/UI/UIInput'
import UIInputError from '../../components/UI/UIInputError'
import UILink from '../../components/UI/UILink'
import UISelect from '../../components/UI/UISelect'
import UISubtitle from '../../components/UI/UISubtitle'
import UITextArea from '../../components/UI/UITextArea'
import UITimePicker from '../../components/UI/UITimePicker'
import UITitle from '../../components/UI/UITitle'
import WidgetMakerHeader from '../../components/Widget/WidgetMakerHeader'
import config from '../../config'
import INT from '../../config/index'
import CREATE_REQUEST from '../../graphql/Requests/mutations/CreateRequest'
import GET_REQUEST_TYPES from '../../graphql/Requests/queries/GetRequestTypes'
import {
  COLOR_BLACK,
  COLOR_GRAY__600,
  COLOR_GRAY__800,
  COLOR_GREY_050,
} from '../../lib/colors'
import { useAnchorAutoScroll } from '../../utils/Utils'
import './style.css'
import { MEDIA_MIN_SM, MEDIA_MIN_XS } from '../../lib/breakpoints'
import { X } from 'react-feather'

const styles = StyleSheet.create({
  containerForm: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '100px',
    '@media (max-width: 480px)': {
      padding: '0 1.5em',
    },
  },
  space: {
    margin: '10px 0px',
  },
  dateTimeContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1em',
    '&div': {
      width: '50%',
    },
    '@media (max-width: 480px)': {
      flexDirection: 'column',
    },
  },
  timeText: {
    fontSize: 11,
    fontWeight: 400,
    color: COLOR_GRAY__600,
    marginTop: '10px',
  },
  spaceBtn: {
    marginTop: '2em',
  },
  subtitle: {
    margin: '20px 0px 10px 0px',
  },
  check: {
    color: 'black',
    flexWrap: 'wrap',
    display: 'block',
  },
  durationConnexion: {
    fontFamily: 'Helvetica',
    fontWeight: 'bold',
    fontSize: '24px',
  },
  punclineUbiqeyes: {
    fontFamily: 'Helvetica',
    fontWeight: '400',
    fontSize: '18px',
    marginTop: '-10px',
  },
  linearGradientDivDescription: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  soonAvailable: {
    marginTop: '0',
    fontFamily: 'Helvetica',
    fontWeight: '400',
    fontSize: '16px',
  },
  alreadyAvailable: {
    textAlign: 'center',
    fontFamily: 'Helvetica',
    fontWeight: 'bold',
    fontSize: '24px',
    marginBottom: '0',
  },
  flexDivForPirce: {
    display: 'flex',
    flexDirection: 'column',
    [MEDIA_MIN_XS]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
  launch: {
    margin: 0,
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  oldPrice: {
    textAlign: 'center',
    color: 'grey',
    margin: 0,
    textDecoration: 'line-through',
    textDecorationColor: 'grey',
  },
  launchMobile: {
    margin: 0,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  priceMobile: {
    margin: 0,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  price: {
    margin: 0,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 30,
  },
  oldPriceMobile: {
    width: '100%',
    textAlign: 'center',
    color: 'grey',
    margin: 0,
    textDecoration: 'line-through',
    textDecorationColor: 'grey',
  },
  form: {
    margin: '20px 0',
    [MEDIA_MIN_SM]: { margin: '20px 40px' },
  },
  addressInput: {
    position: 'relative',
  },
  resetAddress: {
    position: 'absolute',
    background: 'transparent',
    border: 'none',
    right: 0,
    top: 0,
    bottom: 0,
    cursor: 'pointer',
  },
  infos: {
    fontSize: 14,
    fontFamily: 'Helvetica',
    textAlign: 'justify',
    [MEDIA_MIN_SM]: { fontSize: 16 },
  },
  links: {
    color: COLOR_BLACK,
    fontWeight: 'bold',
  },
})

const CreateRequests = () => {
  const { t } = useTranslation('translation')
  const [datePickerValue, setDatePickerValue] = useState(null)
  const [timePickerValue, setTimePickerValue] = useState(null)
  const [hasDateError, setDateError] = useState(false)
  const [hasTimeError, setTimeError] = useState(false)
  const [hasAddressError, setAddressError] = useState(false)
  const [generalConditionChecked, setGeneralConditionChecked] = useState(false)
  const [privacyPoliciesChecked, setPrivacyPoliciesChecked] = useState(false)
  const [chartChecked, setChartChecked] = useState(false)
  const [address, setAddress] = useState('')
  const [fullAddress, setFullAddress] = useState('')
  const [city, setCity] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [country, setCountry] = useState('')
  const [countryError, setCountryError] = useState(false)

  const { isUserLogged, toggleLoginOpened } = useContext(LiveContext)
  useAnchorAutoScroll()

  useEffect(() => {
    ReactPixel.pageView()
  }, [])

  const onDateChange = (date) => {
    setDatePickerValue(date)
  }
  const onTimeChange = (time) => {
    setTimePickerValue(time)
  }

  const [location, setLocation] = useState({
    lat: null,
    lng: null,
  })

  const navigate = useNavigate()

  useEffect(() => {
    navigator.geolocation.getCurrentPosition((position) => {
      setLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      })
    })
  }, [])

  const { data: requestTypesList } = useQuery(GET_REQUEST_TYPES)
  const [createRequest, { loading }] = useMutation(CREATE_REQUEST, {
    onCompleted: (data) => {
      toast.success(t('pages.createRequest.succesMessage'), {
        position: 'top-center',
      })
      localStorage.setItem('idRequest', data?.createRequest?.request?.id)
      localStorage.removeItem('resquetToSend')
      navigate('/payments')
    },
    onError: (errors) => {
      if (
        errors.message ===
        'conflicting key value violates exclusion constraint "request_unique_on_range"'
      ) {
        toast.error(t('pages.createRequest.errorMessage.conflictTimeRange'), {
          position: 'top-center',
        })
      } else {
        toast.error(t('pages.createRequest.errorMessage.requestError'), {
          position: 'top-center',
        })
      }
    },
  })

  Geocode.setApiKey(config.GOOGLE_APP_KEY)
  Geocode.setLanguage('fr')
  Geocode.setRegion('fr')

  const {
    formState: { errors },
    control,
    handleSubmit,
  } = useForm({
    mode: 'all',
  })

  const onSubmit = ({ title, summary, requestType }) => {
    if (!datePickerValue) {
      setDateError(true)
    } else {
      setDateError(false)
    }
    if (!timePickerValue) {
      setTimeError(true)
    } else {
      setTimeError(false)
    }
    if (!address) {
      setAddressError(true)
    } else {
      setAddressError(false)
    }
    if (!datePickerValue || !datePickerValue || !address) {
      return
    }
    if (!isUserLogged) {
      toggleLoginOpened(true)
      return
    }
    createRequest({
      variables: {
        input: {
          request: {
            status: 'NEW',
            title,
            summary,
            location: { x: location.lat, y: location.lng },
            requestAddress: fullAddress,
            requestCity: city,
            requestZipcode: zipCode,
            requestCountry: country,
            datetimeRange: {
              start: {
                value: dayjs(
                  datePickerValue.format('DD/MM/YYYY') +
                    ' ' +
                    timePickerValue.format('HH:mm'),
                  'DD/MM/YYYY HH:mm'
                ).toISOString(),
                inclusive: true,
              },
              end: {
                value: dayjs(
                  datePickerValue.format('DD/MM/YYYY') +
                    ' ' +
                    timePickerValue.format('HH:mm'),
                  'DD/MM/YYYY HH:mm'
                )
                  .add('1', 'hour')
                  .toISOString(),
                inclusive: true,
              },
            },
            idRequestType: requestType,
          },
        },
      },
    })
  }

  const parseAddress = async (location) => {
    const acceptedTerritories = [
      'FR',
      'GP',
      'MQ',
      'GF',
      'YT',
      'RE',
      'PF',
      'BL',
      'MF',
      'PM',
      'WF',
      'NC',
      'TF',
    ]

    Geocode.fromAddress(location.value.description).then(
      (response) => {
        if (
          acceptedTerritories.includes(
            response.results[0].address_components.find((component) =>
              component.types.includes('country')
            )?.short_name
          )
        ) {
          setCountryError(false)
          setFullAddress(response.results[0].formatted_address)
          setCity(
            response.results[0].address_components.find((component) =>
              component.types.includes('locality')
            )?.long_name
          )
          setCountry(
            response.results[0].address_components.find((component) =>
              component.types.includes('country')
            )?.long_name
          )
          setZipCode(
            response.results[0].address_components.find((component) =>
              component.types.includes('postal_code')
            )?.long_name
          )
        } else {
          setCountryError(true)
        }
      },
      (error) => {
        console.log(error)
      }
    )
  }

  useEffect(() => {
    if (address) {
      Geocode.fromAddress(address.value.description).then(
        (response) => {
          const { lat, lng } = response.results[0].geometry.location
          setLocation({ lat: lat, lng: lng })
        },
        (error) => {
          console.log(error)
        }
      )
    }
  }, [address])

  const containerMap = {
    width: '100%',
    height: 354,
  }

  const customStyles = {
    option: (provided) => ({
      ...provided,
      borderBottom: '1px solid grey',
      padding: 10,
    }),
    control: (provided) => ({
      ...provided,
      width: '100%',
      margin: '10px 0',
      border: '1px solid transparent',
      borderRadius: 5,
      backgroundColor: COLOR_GREY_050,
      boxShadow: '0 1px 2px 0 #32324733',
      fontWeight: 400,
      fontSize: 15,
      color: COLOR_GRAY__800,
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: COLOR_GRAY__600,
    }),
    singleValue: (provided) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
      color: COLOR_GRAY__800,
    }),
    valueContainer: (provided) => ({
      ...provided,
      display: 'flex',
      padding: '10px 16px',
      alignItems: 'center',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      display: 'none',
    }),
  }

  return (
    <>
      <WidgetMakerHeader
        handleBecomeMakerClick={() => {}}
        handleGlobeClick={function noRefCheck() {}}
      />
      <div className={css(styles.containerForm)}>
        <LayoutCol lg={8} md={8} xs={10}>
          {false && (
            <UITitle label={t('pages.createRequest.submitMyRequestTitle')} />
          )}
          <div>
            <div className={css(styles.linearGradientDivDescription)}>
              <p className={css(styles.alreadyAvailable)}>
                {t('pages.createRequest.title')}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className={css(styles.form)}>
            <div className={css(styles.infos)}>
              <p>{t('pages.createRequest.desc1')}</p>
              <p>{t('pages.createRequest.desc2')}</p>
              <p>{t('pages.createRequest.desc3')}</p>
              <p>
                {t('pages.createRequest.desc4')}
                <a
                  href={`tel:${t('pages.createRequest.phoneNumber')}`}
                  className={css(styles.links)}
                >
                  {t('pages.createRequest.phoneNumber')}
                </a>
                {t('pages.createRequest.desc5')}
                <a
                  href={`mailto:${t('pages.createRequest.mail')}`}
                  className={css(styles.links)}
                >
                  {t('pages.createRequest.mail')}
                </a>
              </p>
            </div>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <>
                  <UIInput
                    {...field}
                    label={
                      window.innerWidth < 480
                        ? `${t('pages.createRequest.titlePlaceholder')}*`
                        : null
                    }
                    placeholder={
                      window.innerWidth < 480
                        ? `${t('pages.createRequest.titlePlaceholderMobile')}`
                        : `${t('pages.createRequest.titlePlaceholder')}*`
                    }
                    errorMessage={errors.title && errors.title.message}
                  />
                  <span style={{ fontStyle: 'italic', fontSize: '0.8em' }}>
                    {t('pages.createRequest.subtitlePlaceholder')}
                  </span>
                </>
              )}
              rules={{
                required: {
                  value: true,
                  message: t(
                    'pages.createRequest.errorMessage.titleEmptyError'
                  ),
                },
              }}
            />
            <Controller
              name="requestType"
              control={control}
              render={({ field }) => (
                <UISelect
                  {...field}
                  selectLabel={
                    window.innerWidth < 480
                      ? `${t('pages.createRequest.selectPlaceholder')}*`
                      : null
                  }
                  dynamicStyle={styles.space}
                  kind={errors.requestType ? 'error' : 'simple'}
                  placeholder={
                    window.innerWidth < 480
                      ? `${t('pages.createRequest.selectPlaceholderMobile')}`
                      : `${t('pages.createRequest.selectPlaceholder')}*`
                  }
                  data={
                    requestTypesList?.requestTypes?.nodes.length > 0
                      ? requestTypesList?.requestTypes?.nodes.map((type) => ({
                          key: type.id,
                          value: type.id,
                          text: type.name,
                        }))
                      : null
                  }
                  errorMessage={
                    errors.requestType && errors.requestType.message
                  }
                />
              )}
              rules={{
                required: {
                  value: true,
                  message: t(
                    'pages.createRequest.errorMessage.requestTypeEmptyError'
                  ),
                },
              }}
            />

            <Controller
              name="summary"
              control={control}
              render={({ field }) => (
                <UITextArea
                  {...field}
                  dynamicStyle={styles.space}
                  size="small"
                  placeholder={`${t('pages.createRequest.summaryExample')}*`}
                  errorMessage={errors.summary && errors.summary.message}
                />
              )}
              rules={{
                required: {
                  value: true,
                  message: t(
                    'pages.createRequest.errorMessage.summaryEmptyError'
                  ),
                },
              }}
            />

            <UISubtitle
              label={t('pages.createRequest.whenWouldYouLikeToRent')}
              dynamicStyle={styles.subtitle}
            />
            <div className={css(styles.dateTimeContainer)}>
              <div>
                <UIDatePicker
                  errorMessage={
                    hasDateError &&
                    t('pages.createRequest.errorMessage.requestDateEmptyError')
                  }
                  inputValue={datePickerValue}
                  onDateChange={onDateChange}
                  placeholder={t('pages.createRequest.datePickerPlaceholder')}
                  disablePast={true}
                  icon={true}
                />
                <p className={css(styles.timeText)}>
                  {t('pages.createRequest.datePickerText')}
                </p>
              </div>
              <div>
                <UITimePicker
                  errorMessage={
                    hasTimeError &&
                    t('pages.createRequest.errorMessage.requestTimeEmptyError')
                  }
                  inputTimeValue={timePickerValue}
                  onTimeChange={onTimeChange}
                  icon={true}
                  placeholder={t('pages.createRequest.timePickerPlaceholder')}
                />
                <p className={css(styles.timeText)}>
                  {t('pages.createRequest.timePickerMessage')}
                </p>
              </div>
            </div>
            <UISubtitle
              label={t('pages.createRequest.whereDoYouWantToGo')}
              dynamicStyle={styles.space}
            />
            <div className={css(styles.addressInput)}>
              <GooglePlacesAutocomplete
                apiOptions={{ language: 'fr', region: 'fr' }}
                selectProps={{
                  value: address,
                  onChange: (location) => {
                    setAddress(location)
                    parseAddress(location)
                  },
                  styles: customStyles,
                  placeholder: t('pages.createRequest.enterTheFullAddress'),
                }}
              />
              <button
                className={css(styles.resetAddress)}
                type="button"
                onClick={() => setAddress('')}
              >
                <X color={COLOR_GRAY__600} />
              </button>
            </div>
            {hasAddressError && (
              <UIInputError
                errorMessage={t(
                  'pages.createRequest.errorMessage.addressEmptyError'
                )}
              />
            )}
            {countryError && (
              <UIInputError
                errorMessage={t(
                  'pages.createRequest.errorMessage.wrongCountryError'
                )}
              />
            )}
            {location.lat && address && !countryError && (
              <Map
                apiAlreadyLoaded={true}
                center={location}
                zoom={13}
                containerStyle={containerMap}
              />
            )}
            {window.innerWidth > 480 ? (
              <div className="linearGradientDiv">
                <div className={css(styles.flexDivForPirce)}>
                  <div>
                    <p className={css(styles.durationConnexion)}>
                      {t('pages.createRequest.durationConnexion')}
                    </p>
                    <p className={css(styles.punclineUbiqeyes)}>
                      {t('pages.createRequest.punchline')}
                    </p>
                  </div>

                  <div>
                    <div className="linearGradientText">
                      <p className={css(styles.price)}>
                        {t('pages.createRequest.price')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="linearGradientDiv">
                <div className={css(styles.flexDivForPirce)}>
                  <div>
                    <p className={css(styles.durationConnexion)}>
                      {t('pages.createRequest.durationConnexion')}
                    </p>
                    <p className={css(styles.punclineUbiqeyes)}>
                      {t('pages.createRequest.punchline')}
                    </p>
                  </div>

                  <div>
                    <div className="linearGradientText">
                      <p className={css(styles.priceMobile)}>
                        {t('pages.createRequest.price')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className={css(styles.checkContainer)}>
              <Controller
                name="generalCondition"
                control={control}
                render={({ field }) => (
                  <UICheckbox
                    dynamicStyle={styles.check}
                    {...field}
                    onChange={(e) =>
                      setGeneralConditionChecked(e.target.checked)
                    }
                  >
                    {t('pages.registerWatcher.generalCondition1')}&nbsp;
                    <UILink
                      linkStyle="custom"
                      linkTitle={t('pages.registerWatcher.generalCondition2')}
                      redirection={'/cgu.html'}
                      alt={t('pages.home.footer.cguAlt')}
                    ></UILink>
                    &nbsp;
                    {t('pages.registerWatcher.generalCondition3')}
                    <UILink
                      linkStyle="custom"
                      linkTitle={t('pages.registerWatcher.generalCondition4')}
                      redirection={'/assets/cgv.pdf'}
                      alt={t('pages.home.footer.cguAlt')}
                    ></UILink>
                    &nbsp;
                    {t('pages.registerWatcher.generalCondition5')}
                  </UICheckbox>
                )}
              />
              <Controller
                name="privacyPolicies"
                control={control}
                render={({ field }) => (
                  <UICheckbox
                    {...field}
                    onChange={(e) =>
                      setPrivacyPoliciesChecked(e.target.checked)
                    }
                    dynamicStyle={styles.check}
                  >
                    {t('pages.registerWatcher.privacyPolicies1')}&nbsp;
                    <UILink
                      linkStyle="custom"
                      linkTitle={t('pages.registerWatcher.privacyPolicies2')}
                      redirection={'/assets/politique-de-confidentialites.pdf'}
                      alt={t('pages.home.footer.cguAlt')}
                    ></UILink>
                    &nbsp;
                    {t('pages.registerWatcher.privacyPolicies3')}
                  </UICheckbox>
                )}
              />
              <Controller
                name="chart"
                control={control}
                render={({ field }) => (
                  <UICheckbox
                    {...field}
                    onChange={(e) => setChartChecked(e.target.checked)}
                    dynamicStyle={styles.check}
                  >
                    {t('pages.registerWatcher.chart1')}&nbsp;
                    <UILink
                      linkStyle="custom"
                      linkTitle={t('pages.registerWatcher.chart2')}
                      redirection={'/assets/chartes-de-bienveillance.pdf'}
                      alt={t('pages.registerWatcher.chart2')}
                    ></UILink>
                  </UICheckbox>
                )}
              />
            </div>
            <LayoutRow dynamicStyle={styles.spaceBtn}>
              <UIButton
                type="submit"
                kind="button"
                design="tertiary"
                disabled={
                  INT.AUTH_PERM == false
                    ? true
                    : !(
                        generalConditionChecked &&
                        privacyPoliciesChecked &&
                        chartChecked &&
                        fullAddress &&
                        !loading
                      )
                }
                fullWidth
                label={`${t('pages.createRequest.submitMyRequest')}`}
              />
            </LayoutRow>
          </form>
        </LayoutCol>
      </div>
    </>
  )
}

CreateRequests.propTypes = {
  data: PropTypes.array,
  innerProps: PropTypes.array,
  innerRef: PropTypes.array,
  cx: PropTypes.array,
  getStyles: PropTypes.func,
  isDisabled: PropTypes.bool,
  isSelected: PropTypes.bool,
  isFocused: PropTypes.bool,
  className: PropTypes.object,
}

export default CreateRequests
